/**
 * Authenticated fetch helper for Supabase Edge Functions
 * 
 * This ensures we always have a fresh, valid token before making requests.
 * The Supabase SDK's functions.invoke() has a known issue where it can use
 * stale tokens, so we handle this manually.
 */

import { supabase, SUPABASE_ANON_KEY } from "./supabaseClient";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

interface AuthFetchOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
}

/**
 * Make an authenticated request to a Supabase Edge Function
 */
export async function authFetch(
  functionName: string,
  options: AuthFetchOptions = {}
): Promise<Response> {
  // Get fresh session - this will refresh if needed
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (error || !session) {
    throw new Error("Not authenticated");
  }

  const url = `${SUPABASE_URL}/functions/v1/${functionName}`;
  
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session.access_token}`,
      "apikey": SUPABASE_ANON_KEY,
      ...options.headers
    }
  });
}

/**
 * POST to an Edge Function with JSON body
 */
export async function authPost<T = unknown>(
  functionName: string,
  body: unknown,
  options: AuthFetchOptions = {}
): Promise<T> {
  const response = await authFetch(functionName, {
    method: "POST",
    body: JSON.stringify(body),
    ...options
  });

  if (!response.ok) {
    throw new Error(`Function error: ${response.status}`);
  }

  return response.json();
}
